import { marketTrendsLegendStyles } from './styles.js';
import { MARKET_TRENDS_RAMP, MARKET_TRENDS_NO_DATA_COLOR } from '../marketTrendsColors.js';
const METRIC_LABELS = {
    medianSalePrice: {
        title: 'Median Sale Price',
        subtitle: 'Trailing 12 months',
        infoHeading: 'Median Sale Price',
        infoBody: 'Each area is colored by the median price of homes that closed there in the trailing 12 months - blue is below average, red is above.',
    },
    currentForSale: {
        title: 'Current For Sale',
        subtitle: 'Active listings',
        infoHeading: 'Current For Sale',
        infoBody: 'Each area is colored by the median list price of homes currently for sale there - blue is below average, red is above.',
    },
};
function formatPrice(value) {
    if (value >= 1_000_000)
        return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000)
        return `$${Math.round(value / 1000)}K`;
    return `$${Math.round(value)}`;
}
export class MarketTrendsLegend {
    container;
    legendEl = null;
    visible = false;
    scope = 'neighborhoods';
    metric = 'medianSalePrice';
    onMetricChange;
    min = 0;
    center = 0;
    max = 0;
    constructor(options) {
        this.container = options.container;
        this.onMetricChange = options.onMetricChange;
        this.injectStyles();
        this.render();
    }
    injectStyles() {
        if (document.getElementById('market-trends-legend-styles'))
            return;
        const style = document.createElement('style');
        style.id = 'market-trends-legend-styles';
        style.textContent = marketTrendsLegendStyles;
        document.head.appendChild(style);
    }
    render() {
        this.legendEl = document.createElement('div');
        this.legendEl.className = 'market-trends-legend';
        this.legendEl.innerHTML = this.buildInnerHtml();
        this.attachHandlers();
        this.container.appendChild(this.legendEl);
    }
    buildInnerHtml() {
        const scopeLabel = this.scope === 'neighborhoods' ? 'Neighborhoods' : 'Districts';
        const labels = METRIC_LABELS[this.metric];
        const gradientCss = `linear-gradient(to right, ${MARKET_TRENDS_RAMP.join(', ')})`;
        const hasRange = this.max > this.min;
        return `
      <div class="market-trends-legend-header">
        <select class="market-trends-metric-select" title="Choose which metric to display">
          <option value="medianSalePrice" ${this.metric === 'medianSalePrice' ? 'selected' : ''}>Median Sale Price</option>
          <option value="currentForSale" ${this.metric === 'currentForSale' ? 'selected' : ''}>Current For Sale</option>
        </select>
        <span class="market-trends-legend-scope">${scopeLabel}</span>
        <button type="button" class="market-trends-info-btn" title="What does this show?">?</button>
      </div>
      <div class="market-trends-legend-subtitle">${labels.subtitle}</div>
      <div class="market-trends-gradient" style="background: ${gradientCss};"></div>
      <div class="market-trends-gradient-labels">
        <span>${hasRange ? formatPrice(this.min) : '—'}</span>
        <span title="Island average">${hasRange ? formatPrice(this.center) : ''}</span>
        <span>${hasRange ? formatPrice(this.max) : '—'}</span>
      </div>
      <div class="market-trends-no-data-row">
        <div class="market-trends-no-data-swatch" style="background: ${MARKET_TRENDS_NO_DATA_COLOR};"></div>
        <span>Not enough data</span>
      </div>
    `;
    }
    attachHandlers() {
        this.legendEl?.querySelector('.market-trends-info-btn')?.addEventListener('click', () => {
            this.showInfoPopup();
        });
        this.legendEl?.querySelector('.market-trends-metric-select')?.addEventListener('change', (event) => {
            const metric = event.target.value;
            this.metric = metric;
            this.refresh();
            this.onMetricChange?.(metric);
        });
    }
    refresh() {
        if (this.legendEl) {
            this.legendEl.innerHTML = this.buildInnerHtml();
            this.attachHandlers();
        }
    }
    showInfoPopup() {
        const labels = METRIC_LABELS[this.metric];
        const overlay = document.createElement('div');
        overlay.className = 'market-trends-info-overlay';
        const popup = document.createElement('div');
        popup.className = 'market-trends-info-popup';
        popup.innerHTML = `
      <button class="close-btn">&times;</button>
      <h3>${labels.infoHeading}</h3>
      <p>${labels.infoBody}</p>
      <p>The middle of the scale is the island average, not the midpoint of the lowest and highest areas - most areas cluster well below the priciest few, so this keeps the colors spread out instead of everything reading as blue.</p>
      <p>Areas with fewer than 3 data points don't have enough data for a reliable median, and show in gray instead of a color.</p>
    `;
        const close = () => {
            overlay.remove();
            popup.remove();
        };
        overlay.addEventListener('click', close);
        popup.querySelector('.close-btn')?.addEventListener('click', close);
        document.body.appendChild(overlay);
        document.body.appendChild(popup);
    }
    /** Sets which layer's price range the legend is currently describing. */
    setScope(scope) {
        this.scope = scope;
        this.refresh();
    }
    /**
     * Sets which metric the dropdown shows as selected (e.g. to stay in sync
     * if the metric is ever changed from outside the dropdown itself).
     */
    setMetric(metric) {
        this.metric = metric;
        this.refresh();
    }
    getMetric() {
        return this.metric;
    }
    /**
     * Sets the min/center/max of the currently-rendered dataset. `center` is
     * where the ramp's yellow midpoint actually sits (the average, not the
     * linear midpoint of min/max - see getMarketTrendsColor) and gets its own
     * label under the middle of the bar, same position the color itself lands.
     */
    setRange(min, center, max) {
        this.min = min;
        this.center = center;
        this.max = max;
        this.refresh();
    }
    show() {
        this.visible = true;
        this.legendEl?.classList.add('visible');
    }
    hide() {
        this.visible = false;
        this.legendEl?.classList.remove('visible');
    }
    toggle(show) {
        if (show === undefined)
            show = !this.visible;
        if (show)
            this.show();
        else
            this.hide();
    }
    isVisible() {
        return this.visible;
    }
    destroy() {
        this.legendEl?.remove();
        document.getElementById('market-trends-legend-styles')?.remove();
    }
}
//# sourceMappingURL=MarketTrendsLegend.js.map